import React, { useContext, useEffect } from "react";
import { ChatsContext, CurrentChatContext } from "@/components/context";
import { fetchStreamResponse, parseStreamResponse } from "@/lib/utils";
import { IChat, IMessage, SidebarProps } from "@/lib/types";
import { Trash } from "@phosphor-icons/react";
import { useMsal } from "@azure/msal-react";
import { getJWT } from "@/lib/sso";

export const ChatList = ({ sidebar }: SidebarProps) => {
    const { chats, updateChat, deleteChat } = useContext(ChatsContext);
    const { currentChat, updateCurrentChat } = useContext(CurrentChatContext);
    const { instance, accounts } = useMsal();

    useEffect(() => {
        const abortController = new AbortController();
        const handleStream = async () => {
            const summarizeQuery: IMessage = {
                content: [
                    {
                        text: "Summarize this chat in three words, as a headline, in mixed case, but without quotes.",
                    },
                ],
                role: "user",
            };
            const messageStr = JSON.stringify({
                messages: [...(currentChat?.messages || []), summarizeQuery],
            });
            const jwt = await getJWT(instance, accounts);
            // When the API starts responding, add that to the title.
            const stream = await fetchStreamResponse(messageStr, jwt || "", abortController.signal);
            if (stream) {
                const parser = parseStreamResponse(stream);
                let next = await parser.next();
                let responseStr = "";
                while (!next.done) {
                    const line = next.value;
                    // console.log(line);
                    if (line?.contentBlockDelta) {
                        responseStr += line.contentBlockDelta.delta?.text;
                    }
                    next = await parser.next();
                }
                // When the AI summary response stops responding, set the title of that chat to the
                // summary response and add it to the list of chats in state, and to localstorage.
                if (currentChat !== undefined) {
                    const newChat: IChat = { ...currentChat };
                    newChat.title = responseStr;
                    updateCurrentChat(newChat);
                    // Update the chats in state and in local storage.
                    await updateChat(newChat);
                }
            }
        };
        // If that chat doesn't exist in localstorage and this is the first time the AI has responded,
        // send a query to the AI to summarize the chat for a title.
        if (
            currentChat !== undefined &&
            currentChat.messages.length > 0 &&
            currentChat.messages[currentChat.messages.length - 1].role === "assistant" &&
            currentChat.title === undefined
        ) {
            handleStream()
                .then(() => {})
                .catch((err) => {
                    if (err.name !== 'AbortError') {
                        console.error('Error generating chat title:', err);
                        throw err;
                    }
                });
        }
        return () => {
            // console.log(`StreamingResponse cleanup`);
            abortController.abort();
        };
    }, [accounts, chats, currentChat, instance, updateChat, updateCurrentChat]);

    const chatsWithTitles = chats
        .sort((a, b) => b.timestamp - a.timestamp)
        .filter((c) => c.title !== undefined);

    const deleteChatEvt = async (
        evt: React.MouseEvent<HTMLDivElement, MouseEvent>,
        chat: IChat
    ) => {
        evt.stopPropagation();
        await deleteChat(chat.id);
    };

    return (
        <>
            <div className={"flex-1 overflow-y-auto px-2 text-base lg:text-sm"}>
                {chatsWithTitles.length > 0 ? (
                    <div className={"font-bold pb-2 text-sm lg:text-xs ml-2"}>Previous Chats</div>
                ) : (
                    <></>
                )}
                {chatsWithTitles.map((chat) => (
                    <div
                        key={chat.id}
                        className={
                            "flex flex-row p-2 items-center relative cursor-pointer group hover:bg-zinc-700/10 rounded-xl truncate"
                        }
                        onClick={() => {
                            updateCurrentChat(chat);
                            sidebar.setSidebarOpen(false);
                        }}
                    >
                        <div className={"relative flex-1"}>{chat.title || chat.timestamp}</div>
                        <div
                            className={
                                "absolute top-[3px] right-1 w-[30px] h-[30px] flex-none flex lg:hidden group-hover:flex flex-col items-center justify-center opacity-90 lg:opacity-20 hover:opacity-90 hover:bg-red-700/30 rounded-xl"
                            }
                            onClick={(e) => deleteChatEvt(e, chat)}
                        >
                            <Trash size={20} />
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};
